// "1. सामान्य · 2. विकल्प": the two-tab switch of the tense pages, after the reference design. A real tablist: the
// arrow keys and Home / End move between the tabs, and the panel is labelled by the chosen tab.

import type { FC, KeyboardEvent, ReactNode } from 'react'

import { cn } from '@/lib/utils'

export type KalMode = 'samanya' | 'vikalp'

const MODES: { value: KalMode, hi: string, en: string }[] = [
    { value: 'samanya', hi: '१. सामान्य', en: 'Standard' },
    { value: 'vikalp', hi: '२. विकल्प', en: 'Variants' }
]

interface KalTabsProps {
    /** Unique per page: the ids of the tabs and the panel are built from it. */
    id: string
    value: KalMode
    onChange: (value: KalMode) => void
    /** Accessible name of the tablist. */
    label: string
    children: ReactNode
    className?: string
}

export const KalTabs: FC<KalTabsProps> = ({ id, value, onChange, label, children, className }): ReactNode => {
    const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return

        event.preventDefault()

        const index = MODES.findIndex(m => m.value === value)
        let next = index

        if (event.key === 'ArrowRight') next = (index + 1) % MODES.length
        else if (event.key === 'ArrowLeft') next = (index - 1 + MODES.length) % MODES.length
        else if (event.key === 'Home') next = 0
        else next = MODES.length - 1

        onChange(MODES[next].value)
        document.getElementById(`${id}-${MODES[next].value}`)?.focus()
    }

    return (
        <div className={cn('flex flex-col gap-5', className)}>
            <div
                role='tablist'
                aria-label={label}
                onKeyDown={onKeyDown}
                className='inline-flex w-fit max-w-full flex-wrap gap-1 rounded-2xl border border-border bg-card p-1 shadow-card'
            >
                {MODES.map(m => {
                    const active = m.value === value

                    return (
                        <button
                            key={m.value}
                            type='button'
                            role='tab'
                            id={`${id}-${m.value}`}
                            aria-selected={active}
                            aria-controls={`${id}-panel`}
                            tabIndex={active ? 0 : -1}
                            onClick={() => onChange(m.value)}
                            className={cn(
                                'inline-flex min-h-tap items-baseline gap-2 rounded-xl px-5 py-2 text-sm font-semibold transition-colors',
                                active ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
                            )}
                        >
                            <span lang='hi' className='hindi text-[0.9375rem]'>{m.hi}</span>
                            <span lang='en' className={cn('text-xs font-medium', active ? 'text-primary-foreground/80' : 'text-muted-foreground')}>{m.en}</span>
                        </button>
                    )
                })}
            </div>
            <div role='tabpanel' id={`${id}-panel`} aria-labelledby={`${id}-${value}`} tabIndex={0} className='outline-none'>
                {children}
            </div>
        </div>
    )
}
